import { useState, useEffect, lazy, Suspense } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useAmoebaConfig } from '../../hooks/useAmoebaConfig';
import { useTabParam } from '../../hooks/useTabParam';
import PlatformSettingsModal from './PlatformSettingsModal';
import SentEmailsPanel from './SentEmailsPanel';
import ErrorLogsPanel from './ErrorLogsPanel';
import BugReportsPanel from './BugReportsPanel';
// Charts library only loads when Insights is opened.
const InsightsPanel = lazy(() => import('./InsightsPanel'));

// Platform-wide settings menu — SMTP is the only entry today, but this array
// is the extension point for future platform-level settings.
const PLATFORM_SETTINGS_ITEMS = [
    { id: 'smtp', label: 'SMTP Settings' },
    { id: 'attendance-report', label: 'Send attendance report now' },
];

// Runs the daily attendance report immediately (normally 09:00 for the
// previous day) and summarises what happened per tenant.
const runAttendanceReportNow = async () => {
    const date = window.prompt('Send the daily attendance report for which date? (YYYY-MM-DD, blank = yesterday)', '');
    if (date === null) return;
    const force = window.confirm('Resend to tenants that already received this date\'s report?\n\nOK = resend, Cancel = only tenants not yet sent');
    try {
        const { data } = await api.post('jobs/attendance-report', { date: date.trim() || undefined, force });
        const lines = data.results.map(r => `${r.tenant}: ${r.status}${r.emails != null ? ` (${r.emails} emails${r.failed ? `, ${r.failed} failed` : ''})` : ''}${r.reason ? ` — ${r.reason}` : ''}`);
        window.alert(`Attendance report for ${data.date}\n\n${lines.join('\n') || 'No tenants'}`);
    } catch (err) {
        window.alert(err.response?.data?.error || 'Failed to run the attendance report');
    }
};

const api = axios.create({ baseURL: '/api/platform', withCredentials: true });

// Keep in sync with AMOEBA_MODULES in backend/src/config/amoebas.js
const AMOEBA_OPTIONS = ['LEAVE', 'EXPENSE', 'PAYROLL', 'ATTENDANCE', 'PDF', 'HOLIDAY', 'WORKFLOW', 'HR', 'TIME', 'LIFECYCLE'];
const PLAN_TYPES = ['TRIAL', 'STARTER', 'PRO', 'ENTERPRISE'];
const PLAN_DEFAULTS = {
    TRIAL:      { maxUsers: 10 },
    STARTER:    { maxUsers: 25 },
    PRO:        { maxUsers: 100 },
    ENTERPRISE: { maxUsers: 0 },
};

const PLAN_COLORS = {
    TRIAL:      { bg: '#fef3c7', text: '#92400e' },
    STARTER:    { bg: '#dbeafe', text: '#1e40af' },
    PRO:        { bg: '#e0e7ff', text: '#4338ca' },
    ENTERPRISE: { bg: '#f3e8ff', text: '#6b21a8' },
};

const autoSlug = (name) =>
    name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');

const inputStyle = {
    width: '100%', padding: '0.75rem', borderRadius: '0.5rem',
    border: '1px solid var(--input-border)', background: 'var(--input-bg)',
    color: 'var(--text-main)', fontSize: '0.9rem',
};

const labelStyle = { display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '0.375rem' };

function Field({ label, children }) {
    return (
        <div>
            <label style={labelStyle}>{label}</label>
            {children}
        </div>
    );
}

function Badge({ children, bg, text }) {
    return (
        <span style={{
            display: 'inline-block', padding: '0.25rem 0.75rem', borderRadius: '9999px',
            fontSize: '0.75rem', fontWeight: 600, background: bg, color: text, whiteSpace: 'nowrap',
        }}>
            {children}
        </span>
    );
}

function AlertBanner({ ok, message }) {
    return (
        <div style={{
            fontSize: '0.875rem', borderRadius: '0.5rem', padding: '0.5rem 0.75rem', marginBottom: '0.75rem',
            background: ok ? '#dcfce7' : '#fee2e2', color: ok ? '#166534' : '#991b1b',
        }}>
            {message}
        </div>
    );
}

// ─── Amoeba (module) selector — shared by CreateModal and EditDrawer ──────────
function AmoebaSelector({ mode, setMode, selected, toggle }) {
    return (
        <div>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
                {['ALL', 'CUSTOM'].map(m => (
                    <label key={m} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.875rem', cursor: 'pointer', color: 'var(--text-main)' }}>
                        <input type="radio" checked={mode === m} onChange={() => setMode(m)} /> {m === 'ALL' ? 'All modules' : 'Custom'}
                    </label>
                ))}
            </div>
            {mode === 'CUSTOM' && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {AMOEBA_OPTIONS.map(a => {
                        const active = selected.includes(a);
                        return (
                            <label key={a} style={{
                                display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.75rem',
                                padding: '0.375rem 0.75rem', borderRadius: '9999px', cursor: 'pointer',
                                border: active ? '1px solid var(--primary)' : '1px solid var(--border)',
                                background: active ? 'var(--accent-subtle)' : 'var(--bg-secondary)',
                                color: active ? 'var(--primary)' : 'var(--text-muted)',
                            }}>
                                <input type="checkbox" checked={active} onChange={() => toggle(a)} style={{ display: 'none' }} />
                                {a}
                            </label>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ─── Create Modal ─────────────────────────────────────────────────────────────
function CreateModal({ onClose, onSuccess, baseDomain }) {
    const [form, setForm] = useState({
        name: '', slug: '', adminEmail: '', adminName: '', adminPassword: '',
        planType: 'TRIAL', maxUsers: 10, planExpiresAt: '',
    });
    const [amoebaMode, setAmoebaMode] = useState('ALL');
    const [selectedAmoebas, setSelectedAmoebas] = useState([]);
    const [slugStatus, setSlugStatus] = useState(null); // null | 'checking' | 'available' | 'taken'

    useEffect(() => {
        if (!form.slug || form.slug.length < 3) { setSlugStatus(null); return; }
        setSlugStatus('checking');
        const timer = setTimeout(async () => {
            try {
                const res = await api.get(`tenants/slug-check?slug=${form.slug}`);
                setSlugStatus(res.data.available ? 'available' : 'taken');
            } catch {
                setSlugStatus(null);
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [form.slug]);

    const mutation = useMutation({
        mutationFn: (data) => api.post('tenants', data),
        onSuccess: (res) => onSuccess(res.data),
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        if (slugStatus === 'taken') return;
        const enabledAmoebas = amoebaMode === 'ALL' ? 'ALL' : selectedAmoebas.join(',');
        mutation.mutate({ ...form, enabledAmoebas, planExpiresAt: form.planExpiresAt || undefined });
    };

    const toggleAmoeba = (a) =>
        setSelectedAmoebas(prev => prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]);

    const slugIndicator = slugStatus === 'checking'
        ? <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Checking…</span>
        : slugStatus === 'available'
            ? <span style={{ fontSize: '0.75rem', color: '#166534' }}>✓ Available</span>
            : slugStatus === 'taken'
                ? <span style={{ fontSize: '0.75rem', color: '#991b1b' }}>✗ Already taken</span>
                : null;

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
            <div style={{ background: 'var(--surface)', borderRadius: '1rem', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.2)', width: '100%', maxWidth: '32rem', maxHeight: '90vh', overflowY: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1 }}>
                    <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-main)' }}>Create New Tenant</h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '1.5rem', lineHeight: 1 }}>&times;</button>
                </div>
                <form onSubmit={handleSubmit} style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <Field label="Organization Name">
                        <input required value={form.name}
                            onChange={e => setForm(f => ({ ...f, name: e.target.value, slug: autoSlug(e.target.value) }))}
                            style={inputStyle} placeholder="Acme Corp" />
                    </Field>

                    <Field label="Subdomain Slug">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input required value={form.slug}
                                onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
                                style={{ ...inputStyle, flex: 1, fontFamily: 'monospace' }} placeholder="acme" />
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>.{baseDomain}</span>
                        </div>
                        <div style={{ marginTop: '0.25rem', height: '1rem' }}>{slugIndicator}</div>
                    </Field>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                        <Field label="Admin Name">
                            <input required value={form.adminName}
                                onChange={e => setForm(f => ({ ...f, adminName: e.target.value }))}
                                style={inputStyle} placeholder="Jane Doe" />
                        </Field>
                        <Field label="Admin Email">
                            <input required type="email" value={form.adminEmail}
                                onChange={e => setForm(f => ({ ...f, adminEmail: e.target.value }))}
                                style={inputStyle} placeholder="admin@acme.com" />
                        </Field>
                    </div>

                    <Field label="Admin Initial Password">
                        <input required type="password" value={form.adminPassword}
                            onChange={e => setForm(f => ({ ...f, adminPassword: e.target.value }))}
                            style={inputStyle} placeholder="Min 8 characters" minLength={8} />
                    </Field>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                        <Field label="Plan">
                            <select value={form.planType}
                                onChange={e => {
                                    const pt = e.target.value;
                                    setForm(f => ({ ...f, planType: pt, maxUsers: PLAN_DEFAULTS[pt]?.maxUsers ?? 10 }));
                                }}
                                style={inputStyle}>
                                {PLAN_TYPES.map(p => <option key={p}>{p}</option>)}
                            </select>
                        </Field>
                        <Field label="Max Users (0 = unlimited)">
                            <input type="number" min={0} value={form.maxUsers}
                                onChange={e => setForm(f => ({ ...f, maxUsers: parseInt(e.target.value) || 0 }))}
                                style={inputStyle} />
                        </Field>
                    </div>

                    <Field label="Plan Expiry (TRIAL defaults to 14 days if left blank)">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <input type="date" value={form.planExpiresAt}
                                onChange={e => setForm(f => ({ ...f, planExpiresAt: e.target.value }))}
                                style={{ ...inputStyle, flex: 1 }} />
                            <button type="button"
                                onClick={() => {
                                    const d = new Date();
                                    d.setDate(d.getDate() + 14);
                                    setForm(f => ({ ...f, planExpiresAt: d.toISOString().split('T')[0] }));
                                }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', color: 'var(--primary)', whiteSpace: 'nowrap' }}>
                                Set 14-day trial
                            </button>
                        </div>
                    </Field>

                    <Field label="Enabled Amoebas">
                        <AmoebaSelector mode={amoebaMode} setMode={setAmoebaMode} selected={selectedAmoebas} toggle={toggleAmoeba} />
                    </Field>

                    {mutation.error && (
                        <div style={{ fontSize: '0.875rem', color: '#991b1b', background: '#fee2e2', borderRadius: '0.5rem', padding: '0.5rem 0.75rem' }}>
                            {mutation.error.response?.data?.error || 'Failed to create tenant'}
                        </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', paddingTop: '0.5rem' }}>
                        <button type="button" onClick={onClose} className="btn btn-secondary" style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}>
                            Cancel
                        </button>
                        <button type="submit" disabled={mutation.isPending || slugStatus === 'taken'}
                            className="btn btn-primary" style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}>
                            {mutation.isPending ? 'Creating…' : 'Create Tenant'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ─── Edit Drawer ──────────────────────────────────────────────────────────────
function EditDrawer({ tenant, onClose, baseDomain }) {
    const qc = useQueryClient();
    const [tab, setTab] = useState('details');
    const [resetUserId, setResetUserId] = useState(null);
    const [resetPassword, setResetPassword] = useState('');
    const [resetResult, setResetResult] = useState(null);
    const [emailUserId, setEmailUserId] = useState(null);
    const [newEmail, setNewEmail] = useState('');
    const [emailResult, setEmailResult] = useState(null);
    const [purgeConfirm, setPurgeConfirm] = useState('');
    const [dangerResult, setDangerResult] = useState(null);
    const [form, setForm] = useState({
        name: tenant.name,
        allowedDomains: tenant.allowedDomains || '',
        baseCurrency: tenant.baseCurrency || 'INR',
        enabledAmoebas: tenant.enabledAmoebas || 'ALL',
        primaryAuthMethod: tenant.primaryAuthMethod || 'MANUAL',
        authMode: tenant.authMode || 'WHITELIST',
        logoUrl: tenant.logoUrl || '',
        primaryColor: tenant.primaryColor || '', // empty = no brand colour (the theme's own)
        planType: tenant.planType || 'TRIAL',
        maxUsers: tenant.maxUsers ?? 10,
        planExpiresAt: tenant.planExpiresAt ? tenant.planExpiresAt.split('T')[0] : '',
    });
    const [amoebaMode, setAmoebaMode] = useState(tenant.enabledAmoebas === 'ALL' ? 'ALL' : 'CUSTOM');
    const [selectedAmoebas, setSelectedAmoebas] = useState(
        tenant.enabledAmoebas === 'ALL' ? [] : (tenant.enabledAmoebas || '').split(',').filter(Boolean)
    );

    const { data: users = [], isLoading: usersLoading } = useQuery({
        queryKey: ['platform-tenant-users', tenant.id],
        queryFn: () => api.get(`tenants/${tenant.id}/users`).then(r => r.data),
        enabled: tab === 'users',
    });

    const updateMutation = useMutation({
        mutationFn: (data) => api.patch(`tenants/${tenant.id}`, data),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['platform-tenants'] }),
    });

    const resetPasswordMutation = useMutation({
        mutationFn: ({ userId, newPassword }) =>
            api.post(`tenants/${tenant.id}/users/${userId}/reset-password`, { newPassword }),
        onSuccess: (res) => {
            setResetResult({ ok: true, message: res.data.message });
            setResetUserId(null);
            setResetPassword('');
        },
        onError: (err) => setResetResult({ ok: false, message: err.response?.data?.error || 'Reset failed' }),
    });

    const updateEmailMutation = useMutation({
        mutationFn: ({ userId, newEmail }) =>
            api.patch(`tenants/${tenant.id}/users/${userId}/email`, { newEmail }),
        onSuccess: (res) => {
            setEmailResult({ ok: true, message: res.data.message });
            setEmailUserId(null);
            setNewEmail('');
            qc.invalidateQueries({ queryKey: ['platform-tenant-users', tenant.id] });
        },
        onError: (err) => setEmailResult({ ok: false, message: err.response?.data?.error || 'Email update failed' }),
    });

    const resendWelcomeMutation = useMutation({
        mutationFn: () => api.post(`tenants/${tenant.id}/resend-welcome`, {}),
        onSuccess: (res) => setDangerResult({ ok: true, message: `Welcome email sent to ${res.data.sentTo}` }),
        onError: (err) => setDangerResult({ ok: false, message: err.response?.data?.error || 'Send failed' }),
    });

    const purgeMutation = useMutation({
        mutationFn: () => api.delete(`tenants/${tenant.id}/purge`, { data: { confirmSlug: purgeConfirm } }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['platform-tenants'] });
            onClose();
        },
        onError: (err) => setDangerResult({ ok: false, message: err.response?.data?.error || 'Purge failed' }),
    });

    const toggleAmoeba = (a) =>
        setSelectedAmoebas(prev => prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]);

    const handleSave = () => {
        const enabledAmoebas = amoebaMode === 'ALL' ? 'ALL' : selectedAmoebas.join(',');
        // The brand colour is also edited by the tenant's admin: only send it when
        // changed here, so saving other fields never overwrites theirs.
        const { primaryColor, ...rest } = form;
        updateMutation.mutate({
            ...rest,
            ...(primaryColor !== (tenant.primaryColor || '') ? { primaryColor } : {}),
            enabledAmoebas,
            planExpiresAt: form.planExpiresAt || null,
        });
    };

    const setTrialExpiry = () => {
        const d = new Date();
        d.setDate(d.getDate() + 14);
        setForm(f => ({ ...f, planExpiresAt: d.toISOString().split('T')[0] }));
    };

    const isExpired = tenant.planExpiresAt && new Date(tenant.planExpiresAt) < new Date();
    const usersCount = tenant._count?.users ?? 0;
    const planColor = PLAN_COLORS[tenant.planType || 'TRIAL'];

    const TABS = [
        { id: 'details', label: 'Details' },
        { id: 'auth', label: 'Auth' },
        { id: 'plan', label: 'Plan / Licensing' },
        { id: 'users', label: `Users (${usersCount})` },
        { id: 'danger', label: 'Danger Zone' },
    ];

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex' }}>
            <div style={{ flex: 1, background: 'rgba(0,0,0,0.4)' }} onClick={onClose} />
            <div style={{ width: '100%', maxWidth: '36rem', background: 'var(--surface)', boxShadow: '-10px 0 25px -5px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
                    <div>
                        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-main)' }}>{tenant.name}</h2>
                        <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--primary)' }}>{tenant.slug}.{baseDomain}</span>
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '1.5rem', lineHeight: 1, marginTop: '0.125rem' }}>&times;</button>
                </div>

                {/* Tabs */}
                <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', padding: '0 1.5rem', flexShrink: 0, overflowX: 'auto' }}>
                    {TABS.map(t => (
                        <button key={t.id} onClick={() => setTab(t.id)}
                            style={{
                                padding: '0.75rem 0', marginRight: '1.25rem', background: 'none', border: 'none',
                                borderBottom: tab === t.id ? '2px solid var(--primary)' : '2px solid transparent',
                                color: tab === t.id ? 'var(--primary)' : 'var(--text-muted)',
                                fontWeight: tab === t.id ? 600 : 400, fontSize: '0.875rem', cursor: 'pointer', whiteSpace: 'nowrap',
                            }}>
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {/* ── Details ── */}
                    {tab === 'details' && (
                        <>
                            <Field label="Organization Name">
                                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                    style={inputStyle} />
                            </Field>
                            <Field label="Slug (read-only)">
                                <input value={tenant.slug} readOnly
                                    style={{ ...inputStyle, background: 'var(--bg-secondary)', cursor: 'not-allowed', fontFamily: 'monospace' }} />
                            </Field>
                            <Field label="Base Currency">
                                <input value={form.baseCurrency} onChange={e => setForm(f => ({ ...f, baseCurrency: e.target.value }))}
                                    style={inputStyle} placeholder="INR" />
                            </Field>
                            <Field label="Allowed Email Domains (comma-separated)">
                                <input value={form.allowedDomains} onChange={e => setForm(f => ({ ...f, allowedDomains: e.target.value }))}
                                    style={inputStyle} placeholder="acme.com, acme.co.uk" />
                            </Field>
                            <Field label="Logo URL">
                                <input value={form.logoUrl} onChange={e => setForm(f => ({ ...f, logoUrl: e.target.value }))}
                                    style={inputStyle} placeholder="https://..." />
                            </Field>
                            <Field label="Brand colour (the tenant admin can change it too; empty = theme colour)">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <input type="color" value={/^#[0-9a-f]{6}$/i.test(form.primaryColor) ? form.primaryColor : '#2dd4bf'}
                                        onChange={e => setForm(f => ({ ...f, primaryColor: e.target.value }))}
                                        style={{ height: '2.25rem', width: '3.5rem', borderRadius: '0.375rem', border: '1px solid var(--border)', cursor: 'pointer', padding: '2px' }} />
                                    <input value={form.primaryColor} onChange={e => setForm(f => ({ ...f, primaryColor: e.target.value.trim() }))}
                                        placeholder="#7c3aed" maxLength={7} style={{ ...inputStyle, fontFamily: 'monospace', flex: 1 }} />
                                </div>
                            </Field>
                            <Field label="Enabled Modules (Amoebas)">
                                <AmoebaSelector mode={amoebaMode} setMode={setAmoebaMode} selected={selectedAmoebas} toggle={toggleAmoeba} />
                            </Field>
                        </>
                    )}

                    {/* ── Auth ── */}
                    {tab === 'auth' && (
                        <>
                            <Field label="Primary Auth Method">
                                <select value={form.primaryAuthMethod}
                                    onChange={e => setForm(f => ({ ...f, primaryAuthMethod: e.target.value }))}
                                    style={inputStyle}>
                                    <option value="MANUAL">Manual (Password)</option>
                                    <option value="GOOGLE">Google OAuth</option>
                                    <option value="MICROSOFT">Microsoft OAuth</option>
                                    <option value="SAML">SAML SSO</option>
                                </select>
                            </Field>
                            <Field label="User Provisioning Mode">
                                <select value={form.authMode}
                                    onChange={e => setForm(f => ({ ...f, authMode: e.target.value }))}
                                    style={inputStyle}>
                                    <option value="WHITELIST">Whitelist — admin pre-creates users</option>
                                    <option value="AUTO_CREATE">Auto-create on first SSO login</option>
                                </select>
                            </Field>
                            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', background: 'var(--bg-secondary)', borderRadius: '0.5rem', padding: '0.5rem 0.75rem', lineHeight: 1.5 }}>
                                SAML / OAuth credentials are configured per-tenant via that tenant's Admin → Config panel.
                                Changes here take effect on the next login attempt.
                            </p>
                        </>
                    )}

                    {/* ── Plan / Licensing ── */}
                    {tab === 'plan' && (
                        <>
                            {/* Usage summary */}
                            <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '0.5rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.875rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: 'var(--text-muted)' }}>Current Users</span>
                                    <span style={{ fontWeight: 500, color: (tenant.maxUsers > 0 && usersCount >= tenant.maxUsers) ? '#dc2626' : 'var(--text-main)' }}>
                                        {usersCount} / {(tenant.maxUsers === 0) ? '∞' : tenant.maxUsers}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: 'var(--text-muted)' }}>Current Plan</span>
                                    <Badge bg={planColor.bg} text={planColor.text}>{tenant.planType || 'TRIAL'}</Badge>
                                </div>
                                {tenant.planExpiresAt && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ color: 'var(--text-muted)' }}>Plan Expiry</span>
                                        <span style={{ fontWeight: 500, color: isExpired ? '#dc2626' : 'var(--text-main)' }}>
                                            {isExpired ? 'Expired — ' : ''}{new Date(tenant.planExpiresAt).toLocaleDateString()}
                                        </span>
                                    </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: 'var(--text-muted)' }}>Status</span>
                                    <Badge bg={tenant.isActive ? '#dcfce7' : '#fee2e2'} text={tenant.isActive ? '#166534' : '#991b1b'}>
                                        {tenant.isActive ? 'Active' : 'Suspended'}
                                    </Badge>
                                </div>
                            </div>

                            <Field label="Plan Type">
                                <select value={form.planType}
                                    onChange={e => {
                                        const pt = e.target.value;
                                        setForm(f => ({ ...f, planType: pt, maxUsers: PLAN_DEFAULTS[pt]?.maxUsers ?? f.maxUsers }));
                                    }}
                                    style={inputStyle}>
                                    {PLAN_TYPES.map(p => <option key={p}>{p}</option>)}
                                </select>
                            </Field>
                            <Field label="Max Users (0 = unlimited)">
                                <input type="number" min={0} value={form.maxUsers}
                                    onChange={e => setForm(f => ({ ...f, maxUsers: parseInt(e.target.value) || 0 }))}
                                    style={inputStyle} />
                            </Field>
                            <Field label="Plan Expiry Date (blank = never expires)">
                                <input type="date" value={form.planExpiresAt}
                                    onChange={e => setForm(f => ({ ...f, planExpiresAt: e.target.value }))}
                                    style={inputStyle} />
                            </Field>
                            <div style={{ display: 'flex', gap: '0.75rem' }}>
                                <button type="button" onClick={setTrialExpiry}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', color: 'var(--primary)' }}>
                                    Set 14-day trial
                                </button>
                                {form.planExpiresAt && (
                                    <button type="button" onClick={() => setForm(f => ({ ...f, planExpiresAt: '' }))}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                        Clear expiry
                                    </button>
                                )}
                            </div>
                        </>
                    )}

                    {/* ── Users ── */}
                    {tab === 'users' && (
                        usersLoading ? (
                            <div style={{ textAlign: 'center', padding: '3rem 0', color: 'var(--text-muted)' }}>Loading users…</div>
                        ) : (
                            <div>
                                {resetResult && <AlertBanner ok={resetResult.ok} message={resetResult.message} />}
                                {emailResult && <AlertBanner ok={emailResult.ok} message={emailResult.message} />}
                                {users.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '3rem 0', color: 'var(--text-muted)' }}>No users in this tenant</div>
                                ) : (
                                    <div>
                                        {users.map(u => (
                                            <div key={u.id} style={{ padding: '0.75rem 0', borderBottom: '1px solid var(--border)' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <div>
                                                        <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-main)' }}>{u.name}</div>
                                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{u.email}</div>
                                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', opacity: 0.8, marginTop: '0.125rem' }}>
                                                            Joined {new Date(u.createdAt).toLocaleDateString()}
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                                                        <Badge bg="var(--bg-secondary)" text="var(--text-secondary)">{u.role}</Badge>
                                                        <Badge bg={u.isActive ? '#dcfce7' : '#fee2e2'} text={u.isActive ? '#166534' : '#991b1b'}>
                                                            {u.isActive ? 'Active' : 'Inactive'}
                                                        </Badge>
                                                        <button
                                                            onClick={() => { setEmailUserId(emailUserId === u.id ? null : u.id); setNewEmail(u.email); setEmailResult(null); }}
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, color: 'var(--primary)' }}>
                                                            Change email
                                                        </button>
                                                        <button
                                                            onClick={() => { setResetUserId(resetUserId === u.id ? null : u.id); setResetPassword(''); setResetResult(null); }}
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, color: 'var(--primary)' }}>
                                                            Reset password
                                                        </button>
                                                    </div>
                                                </div>
                                                {emailUserId === u.id && (
                                                    <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                        <input type="email" value={newEmail}
                                                            onChange={e => setNewEmail(e.target.value)}
                                                            placeholder="new-email@company.com"
                                                            style={{ ...inputStyle, flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.8rem' }} />
                                                        <button
                                                            disabled={!newEmail || newEmail === u.email || updateEmailMutation.isPending}
                                                            onClick={() => updateEmailMutation.mutate({ userId: u.id, newEmail })}
                                                            className="btn btn-primary" style={{ fontSize: '0.75rem', padding: '0.5rem 0.75rem', whiteSpace: 'nowrap' }}>
                                                            {updateEmailMutation.isPending ? 'Saving…' : 'Save email'}
                                                        </button>
                                                    </div>
                                                )}
                                                {resetUserId === u.id && (
                                                    <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                        <input type="password" value={resetPassword}
                                                            onChange={e => setResetPassword(e.target.value)}
                                                            placeholder="New password (min 8 chars)"
                                                            style={{ ...inputStyle, flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.8rem' }} minLength={8} />
                                                        <button
                                                            disabled={resetPassword.length < 8 || resetPasswordMutation.isPending}
                                                            onClick={() => resetPasswordMutation.mutate({ userId: u.id, newPassword: resetPassword })}
                                                            className="btn btn-primary" style={{ fontSize: '0.75rem', padding: '0.5rem 0.75rem', whiteSpace: 'nowrap' }}>
                                                            {resetPasswordMutation.isPending ? 'Saving…' : 'Set password'}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    )}

                    {/* ── Danger Zone ── */}
                    {tab === 'danger' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            {dangerResult && <AlertBanner ok={dangerResult.ok} message={dangerResult.message} />}

                            <div style={{ border: '1px solid var(--border)', borderRadius: '0.5rem', padding: '1rem' }}>
                                <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-main)', marginBottom: '0.25rem' }}>Resend welcome email</h3>
                                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.75rem', lineHeight: 1.5 }}>
                                    Re-sends the onboarding email (login URL + plan info) to the tenant's oldest admin.
                                    The email tells them to use their existing password.
                                </p>
                                <button onClick={() => { setDangerResult(null); resendWelcomeMutation.mutate(); }}
                                    disabled={resendWelcomeMutation.isPending}
                                    className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.5rem 0.75rem' }}>
                                    {resendWelcomeMutation.isPending ? 'Sending…' : 'Resend welcome email'}
                                </button>
                            </div>

                            <div style={{ border: '1px solid #fecaca', borderRadius: '0.5rem', padding: '1rem', background: 'rgba(239, 68, 68, 0.06)' }}>
                                <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#dc2626', marginBottom: '0.25rem' }}>Permanently delete this tenant</h3>
                                <p style={{ fontSize: '0.75rem', color: '#dc2626', opacity: 0.85, marginBottom: '0.75rem', lineHeight: 1.5 }}>
                                    Deletes the tenant and ALL of its data — users, leaves, expenses, payroll, holidays,
                                    configuration. This cannot be undone. Type <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{tenant.slug}</span> to confirm.
                                </p>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input value={purgeConfirm} onChange={e => setPurgeConfirm(e.target.value)}
                                        placeholder={tenant.slug}
                                        style={{ ...inputStyle, flex: 1, fontFamily: 'monospace' }} />
                                    <button
                                        disabled={purgeConfirm !== tenant.slug || purgeMutation.isPending}
                                        onClick={() => { setDangerResult(null); purgeMutation.mutate(); }}
                                        style={{
                                            fontSize: '0.75rem', padding: '0.5rem 0.75rem', borderRadius: '0.5rem', border: 'none',
                                            whiteSpace: 'nowrap', cursor: purgeConfirm !== tenant.slug ? 'not-allowed' : 'pointer',
                                            background: '#dc2626', color: 'white',
                                            opacity: (purgeConfirm !== tenant.slug || purgeMutation.isPending) ? 0.5 : 1,
                                        }}>
                                        {purgeMutation.isPending ? 'Deleting…' : 'Permanently delete'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                {tab !== 'users' && tab !== 'danger' && (
                    <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                        <div style={{ fontSize: '0.75rem' }}>
                            {updateMutation.isSuccess && <span style={{ color: '#166534' }}>Saved.</span>}
                            {updateMutation.error && (
                                <span style={{ color: '#991b1b' }}>
                                    {updateMutation.error.response?.data?.error || 'Save failed'}
                                </span>
                            )}
                        </div>
                        <button onClick={handleSave} disabled={updateMutation.isPending}
                            className="btn btn-primary" style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}>
                            {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function TenantManagement() {
    const qc = useQueryClient();
    const { config } = useAmoebaConfig();
    const baseDomain = config.baseDomain || 'junkyardamoeba.com';
    // ?view=bugs (the link in bug report emails) opens that tab directly; choosing another tab updates the address.
    const [view, setView] = useTabParam(['tenants', 'insights', 'bugs', 'emails', 'errors'], 'tenants', 'view');
    const [showCreate, setShowCreate] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showSettingsMenu, setShowSettingsMenu] = useState(false);
    const [editTenant, setEditTenant] = useState(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [createdInfo, setCreatedInfo] = useState(null);

    const { data: tenants = [], isLoading } = useQuery({
        queryKey: ['platform-tenants'],
        queryFn: () => api.get('tenants').then(r => r.data),
    });

    const { data: metrics = [] } = useQuery({
        queryKey: ['platform-metrics'],
        queryFn: () => api.get('metrics').then(r => r.data),
    });
    const metricsByTenant = Object.fromEntries(metrics.map(m => [m.tenantId, m]));
    const totals = {
        tenants: metrics.length,
        active: metrics.filter(m => m.isActive).length,
        users: metrics.reduce((s, m) => s + m.activeUsers, 0),
        leaves: metrics.reduce((s, m) => s + m.leavesThisMonth, 0),
        expenses: metrics.reduce((s, m) => s + m.expensesThisMonth, 0),
    };

    const suspendMutation = useMutation({
        mutationFn: (id) => api.delete(`tenants/${id}`),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['platform-tenants'] }),
    });

    const reactivateMutation = useMutation({
        mutationFn: (id) => api.post(`tenants/${id}/reactivate`),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['platform-tenants'] }),
    });

    const filtered = tenants.filter(t => {
        const q = search.toLowerCase();
        const matchesSearch = !q || t.name.toLowerCase().includes(q) || t.slug.includes(q);
        const matchesStatus = statusFilter === 'all'
            || (statusFilter === 'active' ? t.isActive : !t.isActive);
        return matchesSearch && matchesStatus;
    });

    // Keep edit drawer in sync with latest tenant data
    const liveEditTenant = editTenant ? tenants.find(t => t.id === editTenant.id) || editTenant : null;

    const rowHoverOn = (e) => { e.currentTarget.style.background = 'var(--surface-hover)'; };
    const rowHoverOff = (e) => { e.currentTarget.style.background = 'transparent'; };

    return (
        <div className="container" style={{ maxWidth: '80rem' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                <div>
                    <h1 style={{ marginBottom: '0.125rem' }}>Tenant Management</h1>
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>JA Technology Labs — Platform Admin</p>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <div style={{ position: 'relative' }}>
                        <button
                            onClick={() => setShowSettingsMenu(v => !v)}
                            onBlur={() => setTimeout(() => setShowSettingsMenu(false), 200)}
                            className="btn btn-secondary"
                        >
                            Platform Settings ▾
                        </button>
                        {showSettingsMenu && (
                            <div style={{
                                position: 'absolute', top: '110%', right: 0, background: 'var(--surface)',
                                border: '1px solid var(--border)', borderRadius: '0.75rem', boxShadow: 'var(--shadow)',
                                padding: '0.5rem', minWidth: '180px', zIndex: 20,
                            }}>
                                {PLATFORM_SETTINGS_ITEMS.map(item => (
                                    <button
                                        key={item.id}
                                        onClick={() => {
                                            setShowSettingsMenu(false);
                                            if (item.id === 'attendance-report') runAttendanceReportNow();
                                            else setShowSettings(true);
                                        }}
                                        style={{
                                            display: 'block', width: '100%', textAlign: 'left', padding: '0.5rem 0.75rem',
                                            border: 'none', background: 'transparent', color: 'var(--text-primary)',
                                            borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.875rem',
                                        }}
                                        onMouseOver={(e) => e.currentTarget.style.background = 'var(--surface-hover)'}
                                        onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <button onClick={() => setShowCreate(true)} className="btn btn-primary">
                        + New Tenant
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.5rem', borderBottom: '1px solid var(--border)' }}>
                {[{ id: 'tenants', label: 'Tenants' }, { id: 'insights', label: 'Insights' }, { id: 'bugs', label: 'Bug Reports' }, { id: 'emails', label: 'Sent Emails' }, { id: 'errors', label: 'Error Logs' }].map(t => (
                    <button
                        key={t.id}
                        onClick={() => setView(t.id)}
                        style={{
                            padding: '0.75rem 0', border: 'none', background: 'none', cursor: 'pointer',
                            borderBottom: view === t.id ? '2px solid var(--primary)' : '2px solid transparent',
                            color: view === t.id ? 'var(--primary)' : 'var(--text-muted)',
                            fontWeight: view === t.id ? 600 : 400, fontSize: '0.9rem', marginBottom: '-1px',
                        }}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {view === 'insights' ? (
                <Suspense fallback={<p style={{ color: 'var(--text-muted)' }}>Loading insights…</p>}><InsightsPanel /></Suspense>
            ) : view === 'bugs' ? (
                <BugReportsPanel tenants={tenants} />
            ) : view === 'emails' ? (
                <SentEmailsPanel tenants={tenants} />
            ) : view === 'errors' ? (
                <ErrorLogsPanel tenants={tenants} />
            ) : (
            <>
            {/* Creation success banner */}
            {createdInfo && (
                <div style={{ marginBottom: '1rem', background: '#dcfce7', border: '1px solid #86efac', borderRadius: '0.5rem', padding: '0.75rem 1rem', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
                    <p style={{ fontSize: '0.875rem', color: '#166534' }}>
                        <strong>{createdInfo.tenant?.name}</strong> created. Welcome email queued to{' '}
                        <strong>{createdInfo.adminEmail}</strong>. Login URL:{' '}
                        <span style={{ fontFamily: 'monospace' }}>{createdInfo.loginUrl}</span>
                    </p>
                    <button onClick={() => setCreatedInfo(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#166534', fontSize: '1.125rem', lineHeight: 1, flexShrink: 0 }}>&times;</button>
                </div>
            )}

            {/* Metrics strip — stat tiles: label (sentence case) + semibold value, no delta/trend (no historical baseline yet) */}
            {metrics.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
                    {[
                        { label: 'Tenants', value: totals.tenants },
                        { label: 'Active tenants', value: totals.active },
                        { label: 'Active users', value: totals.users },
                        { label: 'Leaves this month', value: totals.leaves },
                        { label: 'Expenses this month', value: totals.expenses },
                    ].map(s => (
                        <div key={s.label} style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '0.75rem', padding: '1rem' }}>
                            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-main)' }}>{s.value}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>{s.label}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Filters */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <input value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Search by name or slug…"
                    style={{ ...inputStyle, width: '16rem', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }} />
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                    style={{ ...inputStyle, width: 'auto', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}>
                    <option value="all">All Statuses</option>
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                </select>
                <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>{filtered.length} tenant{filtered.length !== 1 ? 's' : ''}</span>
            </div>

            {/* Table */}
            {isLoading ? (
                <div style={{ textAlign: 'center', padding: '3rem 0', color: 'var(--text-muted)' }}>Loading tenants…</div>
            ) : (
                <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.875rem' }}>
                            <thead style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)' }}>
                                <tr>
                                    {['Organization', 'Slug', 'Plan', 'Users', 'Modules', 'Expires', 'Last active', 'Status', 'Actions'].map(h => (
                                        <th key={h} style={{ padding: '0.75rem 1rem', fontWeight: 600, color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(t => {
                                    const isExpired = t.planExpiresAt && new Date(t.planExpiresAt) < new Date();
                                    const usersCount = t._count?.users ?? 0;
                                    const atLimit = t.maxUsers > 0 && usersCount >= t.maxUsers;
                                    const lastActive = metricsByTenant[t.id]?.lastActiveAt;
                                    const lastLogin = metricsByTenant[t.id]?.lastLoginAt;
                                    const planColor = PLAN_COLORS[t.planType || 'TRIAL'];
                                    return (
                                        <tr key={t.id} style={{ borderBottom: '1px solid var(--border)' }} onMouseOver={rowHoverOn} onMouseOut={rowHoverOff}>
                                            <td style={{ padding: '0.75rem 1rem' }}>
                                                <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>{t.name}</div>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>{t.id.slice(0, 8)}…</div>
                                            </td>
                                            <td style={{ padding: '0.75rem 1rem', fontFamily: 'monospace', color: 'var(--primary)', fontSize: '0.8rem' }}>{t.slug}</td>
                                            <td style={{ padding: '0.75rem 1rem' }}>
                                                <Badge bg={planColor.bg} text={planColor.text}>{t.planType || 'TRIAL'}</Badge>
                                            </td>
                                            <td style={{ padding: '0.75rem 1rem' }}>
                                                <span style={{ fontSize: '0.8rem', fontWeight: atLimit ? 600 : 400, color: atLimit ? '#dc2626' : 'var(--text-secondary)' }}>
                                                    {usersCount} / {t.maxUsers === 0 ? '∞' : t.maxUsers}
                                                </span>
                                            </td>
                                            <td style={{ padding: '0.75rem 1rem' }}>
                                                <Badge bg="var(--bg-secondary)" text="var(--text-secondary)">{t.enabledAmoebas || 'ALL'}</Badge>
                                            </td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem' }}>
                                                {t.planExpiresAt ? (
                                                    <span style={{ color: isExpired ? '#dc2626' : 'var(--text-secondary)', fontWeight: isExpired ? 600 : 400 }}>
                                                        {isExpired ? 'Expired ' : ''}{new Date(t.planExpiresAt).toLocaleDateString()}
                                                    </span>
                                                ) : (
                                                    <span style={{ color: 'var(--text-muted)' }}>Never</span>
                                                )}
                                            </td>
                                            <td style={{ padding: '0.75rem 1rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}
                                                title={lastLogin ? `Last sign-in: ${new Date(lastLogin).toLocaleString()}` : 'No sign-in recorded'}>
                                                {lastActive ? new Date(lastActive).toLocaleDateString() : '—'}
                                            </td>
                                            <td style={{ padding: '0.75rem 1rem' }}>
                                                <Badge bg={t.isActive ? '#dcfce7' : '#fee2e2'} text={t.isActive ? '#166534' : '#991b1b'}>
                                                    {t.isActive ? 'Active' : 'Suspended'}
                                                </Badge>
                                            </td>
                                            <td style={{ padding: '0.75rem 1rem' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                    <button onClick={() => setEditTenant(t)}
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, color: 'var(--primary)' }}>
                                                        Edit
                                                    </button>
                                                    {t.isActive ? (
                                                        <button
                                                            onClick={() => { if (confirm(`Suspend ${t.name}?`)) suspendMutation.mutate(t.id); }}
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', color: '#dc2626' }}>
                                                            Suspend
                                                        </button>
                                                    ) : (
                                                        <button onClick={() => reactivateMutation.mutate(t.id)}
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', color: '#16a34a' }}>
                                                            Reactivate
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={9} style={{ padding: '2.5rem 1rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                                            {search || statusFilter !== 'all' ? 'No tenants match your filters' : 'No tenants yet'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            </>
            )}

            {showCreate && (
                <CreateModal
                    baseDomain={baseDomain}
                    onClose={() => setShowCreate(false)}
                    onSuccess={(data) => {
                        setShowCreate(false);
                        setCreatedInfo(data);
                        qc.invalidateQueries({ queryKey: ['platform-tenants'] });
                        qc.invalidateQueries({ queryKey: ['platform-metrics'] });
                    }}
                />
            )}

            {liveEditTenant && (
                <EditDrawer
                    key={liveEditTenant.id}
                    tenant={liveEditTenant}
                    baseDomain={baseDomain}
                    onClose={() => setEditTenant(null)}
                />
            )}

            {showSettings && <PlatformSettingsModal onClose={() => setShowSettings(false)} />}
        </div>
    );
}
